import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Landmark, Loader2, Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useTribeAuth } from "@/context/TribeAuthContext";
import { LOGO_URL } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function TribeAuth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useTribeAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const plan = params.get("plan");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ tribe_name: "", nation: "", contact_name: "", email: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const dest = plan ? `/tribes/dashboard?plan=${plan}` : "/tribes/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!form.tribe_name || !form.email || form.password.length < 8) {
          toast.error("Nation name, email, and a password of at least 8 characters are required.");
          setBusy(false);
          return;
        }
        const domain = form.email.toLowerCase().split("@")[1] || "";
        if (!(domain.endsWith(".gov") || domain.endsWith(".org"))) {
          toast.error("Nation accounts require an official .gov or .org email address.");
          setBusy(false);
          return;
        }
        await register(form);
        toast.success("Nation account created.");
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      }
      navigate(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-3 mt-2 font-sans text-[15px] text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors";

  return (
    <div className="min-h-screen bg-ink flex flex-col items-center justify-center px-6 py-20">
      <Link to="/" className="mb-10">
        <img src={LOGO_URL} alt="7 Generations Indigenous Trust" className="h-20 w-auto object-contain" />
      </Link>
      <div className="w-full max-w-md border border-gold/20 bg-ink-surface p-8 sm:p-10" data-testid="tribe-auth-card">
        <Landmark size={28} className="text-gold" strokeWidth={1.3} />
        <h1 className="font-serif text-3xl mt-5">{mode === "register" ? "Register Your Nation" : "Tribal Government Login"}</h1>
        <p className="font-sans text-sm text-[#A19D98] mt-3">
          {mode === "register"
            ? "Create a secure, sovereign account for your nation's records repository."
            : "Access your nation's records repository."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="tribe-auth-form">
          {mode === "register" && (
            <>
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Nation / Tribe Name *</label>
                <input data-testid="tribe-name-input" value={form.tribe_name} onChange={set("tribe_name")} className={inputCls} placeholder="e.g. Woodland Confederacy" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Heritage / Region</label>
                <input data-testid="tribe-nation-input" value={form.nation} onChange={set("nation")} className={inputCls} placeholder="(optional)" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Authorized Contact Name</label>
                <input data-testid="tribe-contact-input" value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="(optional)" />
              </div>
            </>
          )}
          <div>
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Government Email *</label>
            <input data-testid="tribe-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="records@yournation.gov" />
            {mode === "register" && (
              <p className="font-sans text-[11px] text-[#7a766f] mt-1.5">Official .gov or .org email required.</p>
            )}
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Password *</label>
            <input data-testid="tribe-password-input" type="password" value={form.password} onChange={set("password")} className={inputCls} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} />
          </div>
          {mode === "register" && (
            <div className="border-t border-gold/15 pt-5" data-testid="tribe-nda-clause">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-gold shrink-0 mt-0.5" strokeWidth={1.4} />
                <p className="font-sans text-[11px] text-[#8f8b86] leading-relaxed">
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-gold/70 block mb-1">Non-Disclosure Agreement</span>
                  By creating a nation account, you acknowledge that all information exchanged with the 7 Generations
                  Indigenous Trust — including your tribal rolls, citizen registry, and governing documents — is
                  strictly confidential and protected under a mutual Non-Disclosure Agreement. You further agree not
                  to disclose any information about the Trust received by your nation to any third party without
                  verifiable written or digital proof of authorization bearing the official seal of the Trust.
                  Creating this account constitutes your authorized representative's electronic signature and binding
                  acceptance of these NDA terms.
                </p>
              </div>
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="tribe-auth-submit" className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Please wait…</> : <>{mode === "register" ? "Create Account" : "Sign In"} <ArrowRight size={14} /></>}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gold/10 text-center">
          <button
            data-testid="tribe-auth-toggle"
            onClick={() => setMode(mode === "register" ? "login" : "register")}
            className="font-mono text-[11px] tracking-[0.16em] uppercase text-gold/70 hover:text-gold transition-colors"
          >
            {mode === "register" ? "Already registered? Sign in" : "New nation? Register here"}
          </button>
        </div>
      </div>
      <Link to="/tribal-portal" className="mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a766f] hover:text-gold transition-colors flex items-center gap-2">
        <Lock size={12} /> Learn about the Tribal Records Repository
      </Link>
    </div>
  );
}
